import { ReactComponent as SuccessIcon } from '@assets/images/ttm/greenCircle.svg';
import { ReactComponent as UnsuccessfulIcon } from '@assets/images/ttm/redCircle.svg';
import NewModalConfirm from '@components/shared/NewModalConfirm';
import {
	ACTIVITY_STATUS,
	PAYOUT_METHOD,
	TIMESTAMP_DESCRIPTION,
	TIMESTAMP_DESCRIPTION_OVERRIDES,
	TIMESTAMP_HEADER,
	TIMESTAMP_HEADER_OVERRIDES,
	TIMESTAMP_TOOLTIP
} from '@constant/steps';
import { TransferDetailsContext } from '@context/TransferDetailsContext';
import useNewModal from '@effects/useNewModal';
import { formatDateWithTime } from '@helpers/Date';
import DoneIcon from '@mui/icons-material/Done';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Typography, useTheme } from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { TransactionDetailsModalContent, TransactionDetailsModalHeader } from './TransactionDetailsModalContent';

const useStyles = makeStyles()((theme) => ({
	circle: {
		width: 24,
		height: 24,
		borderRadius: '90%'
	},
	dialogRootClass: {
		maxWidth: '416px'
	}
}));

const CustomStepIcon = (status) => {
	const theme = useTheme();
	return function Icon() {
		const { classes } = useStyles();

		if (status === ACTIVITY_STATUS.SUCCESS) {
			return <DoneIcon sx={{ color: theme.palette.success.main }} />;
		} else if (status === ACTIVITY_STATUS.IN_PROGRESS) {
			return <SuccessIcon className={classes.circle} />;
		} else if (status === ACTIVITY_STATUS.UNSUCCESSFUL) {
			return <UnsuccessfulIcon className={classes.circle} />;
		}
	};
};

const NoTransition = ({ children }) => <>{children}</>;

function CustomStepper() {
	const theme = useTheme();
	const { t } = useTranslation();
	const { classes } = useStyles();
	const { details, timeline }: any = useContext(TransferDetailsContext);
	const { modalStatus, modalTitle, modalContent, modalButtons, updateModalContent, closeModal } = useNewModal();

	const handleClick = () => {
		updateModalContent(<TransactionDetailsModalHeader closeModal={closeModal} />, <TransactionDetailsModalContent />, [
			{
				label: t('OKAY_GOT_IT'),
				color: 'accent',
				variant: 'outlined',
				func: closeModal,
				styles: { height: '55px', color: theme.text?.primary, borderColor: theme.text?.primary }
			}
		]);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Stepper activeStep={-1} orientation="vertical" nonLinear>
					{timeline &&
						details &&
						timeline
							.slice()
							.reverse()
							.map((data, index) => {
								const payoutMethod = PAYOUT_METHOD[details.transaction.payoutMethod.toUpperCase()];
								const { state, action, status } = data.activity;

								return (
									<Step key={index}>
										<StepLabel StepIconComponent={CustomStepIcon(status)}>
											<Box sx={{ pl: '16px', pt: '0px', mt: '0px' }}>
												{data.showTimestamp && (
													<Typography variant={'h6'} fontStyle="bold" color={theme.surfaces.level5} sx={{ fontWeight: '700' }}>
														{formatDateWithTime(data.timestamp)}
													</Typography>
												)}
												{state && (
													<Typography
														variant={index === timeline.length - 1 ? 'h4' : 'h6'}
														fontStyle="bold"
														color={index === timeline.length - 1 ? theme.text.primary : theme.text.secondary}
														sx={{ fontWeight: '700' }}>
														{t(
															index === timeline.length - 1
																? (TIMESTAMP_HEADER_OVERRIDES?.[state]?.[action]?.[payoutMethod] ?? TIMESTAMP_HEADER?.[state]?.[action])
																: TIMESTAMP_HEADER?.[state]?.[action]
														)}
													</Typography>
												)}
											</Box>
										</StepLabel>
										<StepContent TransitionComponent={NoTransition}>
											<Box sx={{ pl: '16px', pt: '0px' }}>
												{!TIMESTAMP_TOOLTIP?.[state]?.[action]?.[payoutMethod] && (
													<Typography variant="h6" color={theme.text.secondary} sx={{ fontWeight: '700' }}>
														{t(TIMESTAMP_DESCRIPTION_OVERRIDES?.[state]?.[action]?.[payoutMethod] ?? TIMESTAMP_DESCRIPTION?.[state]?.[action])}
													</Typography>
												)}
												{TIMESTAMP_TOOLTIP?.[state]?.[action]?.[payoutMethod] && (
													<Box
														sx={{
															display: 'inline-flex',
															alignItems: 'center',
															textDecoration: 'underline',
															color: theme.palette.secondary.main
														}}>
														<ButtonBase onClick={handleClick}>
															<Typography
																variant="h6"
																sx={{ fontWeight: '700', color: theme.palette.secondary.main, textDecoration: 'underline', m: 0 }}>
																{t(TIMESTAMP_DESCRIPTION_OVERRIDES?.[state]?.[action]?.[payoutMethod] ?? TIMESTAMP_DESCRIPTION?.[state]?.[action])}
															</Typography>
															<ErrorOutlineIcon sx={{ color: theme.palette.secondary.main, ml: 1, fontSize: '20px', transform: 'rotate(180deg)' }} />
														</ButtonBase>
													</Box>
												)}
											</Box>
										</StepContent>
									</Step>
								);
							})}
				</Stepper>
			</Box>
			<NewModalConfirm
				open={modalStatus}
				handleClose={closeModal}
				title={modalTitle}
				content={modalContent}
				buttons={modalButtons}
				customStyles={{ dialogRootClass: classes.dialogRootClass }}
			/>
		</>
	);
}

export default CustomStepper;
